import { ErrorBoundary, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useCallback } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';

import { useWithdrawalActions } from '../../../../../../store/customerDetail/withdrawal/actions';
import { CustomerWithdrawalItemNormalized } from '../../../../../../store/customerDetail/withdrawal/types';
import { WithdrawalEmpty } from '../WithdrawalEmpty';
import { WithdrawalHeader } from '../WithdrawalHeader';
import { WithdrawalItem } from '../WithdrawalItem';
import { WithdrawalLoading } from '../WithdrawalLoading';

export namespace ListTypes {
  export type Props = {
    isMobile?: boolean;
    data: CustomerWithdrawalItemNormalized[];
    isLoading: boolean;
    onEndReached: VoidFunction;
    error?: Error;
    refresher: VoidFunction;
  };
}

const styles = StyleSheet.create({
  container: {
    marginLeft: -Utils.Token.ToInt(Spacing, 'inset.xs'),
    marginRight: -Utils.Token.ToInt(Spacing, 'inset.xs'),
  },
  containerDesktop: {
    maxHeight: 400,
    paddingBottom: Utils.Token.ToInt(Spacing, 'inset.xs'),
  },
  containerMobile: {
    flex: 1,
  },
  separator: {
    height: Utils.Token.ToInt(Spacing, 'inset.xs'),
  },
});

const Separator = () => <View style={styles.separator} />;

export const WithdrawalList: React.FC<ListTypes.Props> = ({
  isMobile,
  isLoading,
  data,
  refresher,
  error,
  onEndReached,
}) => {
  const { downloadReceipt, cancelWithdrawal } = useWithdrawalActions();
  const handleDownloadReceipt = useCallback(
    (id: number | string, accountId: number | string) => {
      downloadReceipt(id, accountId);
    },
    [downloadReceipt]
  );

  const handleCancel = useCallback(
    (id: number | string, accountId: number | string) => {
      cancelWithdrawal(id, accountId);
    },
    [cancelWithdrawal]
  );

  if (error) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  return (
    <VStack space={Spacing['stack.quarck']}>
      {!isMobile && <WithdrawalHeader />}

      <FlatList
        data={data}
        style={[styles.container, isMobile ? styles.containerMobile : styles.containerDesktop]}
        onEndReachedThreshold={0.4}
        onEndReached={onEndReached}
        ItemSeparatorComponent={Separator}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <WithdrawalItem
            item={item}
            onDownloadReceipt={handleDownloadReceipt}
            onCancel={handleCancel}
          />
        )}
        ListEmptyComponent={isLoading ? <WithdrawalLoading /> : <WithdrawalEmpty />}
        ListFooterComponent={isLoading && data.length ? <WithdrawalLoading /> : null}
      />
    </VStack>
  );
};
